import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import classnames from 'classnames/bind'
import css from './styles.module.scss'
import DollarSign from 'components/shared/DollarSign'
import CheckMark from 'components/icons/CheckMark'
import Tooltip from 'components/shared/Tooltip'
import Button from 'components/shared/Button'
import InputNumber from '../InputNumber'
import { Text } from 'src/components/typo'
import { ReactComponent as Cross } from 'components/shared/Header/Mobile/Overlay/cross.svg'
import { GROWTH_PLAN_PRICE_ID, GROWTH_PLAN_SSO_PRICE_ID } from './Constants'
import { Formik } from 'formik'

const cx = classnames.bind(css)

const BuyGrowthModal = ({
  isModalOpen,
  closeModal,
  spreadedPlan,
  starFeatures,
  modalRef,
}) => {
  const [seats, setSeats] = useState(1)
  const [isToggleOn, setIsToggleOn] = useState(false)

  useEffect(() => {
    if (!isModalOpen) {
      setSeats(1)
      setIsToggleOn(false)
    }
  }, [isModalOpen])

  if (!isModalOpen) return null

  const handleSubmit = (e) => {
    e.preventDefault()

    const additionalSeats = seats - 1

    const baseUrl = process.env.NEXT_PUBLIC_CHARGEBEE_URL
    const itemPriceId = isToggleOn
      ? GROWTH_PLAN_SSO_PRICE_ID
      : GROWTH_PLAN_PRICE_ID

    const params = new URLSearchParams({
      'subscription_items[item_price_id][0]': itemPriceId,
      ...(additionalSeats > 0
        ? {
            'subscription_items[item_price_id][1]':
              'additional-seat-USD-Monthly',
            'subscription_items[quantity][1]': additionalSeats,
          }
        : {}),
      layout: 'full_page',
    })

    const url = `${baseUrl}/hosted_pages/checkout?${params.toString()}`

    try {
      new URL(url)
      window.location.href = url
    } catch (err) {
      console.error('An error occurred while processing your request.')
    }
  }

  return createPortal(
    <div className={css.buyGrowthModal__overlay}>
      <form onSubmit={handleSubmit}>
        <div ref={modalRef} className={css.buyGrowthModal__modal}>
          <div className={css.header}>
            <Cross
              className={css.cross}
              onClick={(e) => {
                e.preventDefault()
                closeModal()
              }}
            />
            <Text theme="dark" size="big" className={css.header__title}>
              Add seats to your plan
            </Text>
          </div>
          <div className={css.buyGrowthModal__container}>
            <div className={css.buyGrowthModal__content}>
              <div className={css.buyGrowthModal__details}>
                <Text
                  className={cx(css.planTitle, css.titleMobile)}
                  size="smaller"
                  theme="dark"
                >
                  {spreadedPlan.name}
                </Text>
                <Text
                  className={cx(css.planTitle, css.titleMobile, css.title)}
                  size="smaller"
                >
                  SELF-HOSTED
                </Text>
              </div>
              <div className={cx(css.buyGrowthModal__details, css.planDetail)}>
                <Text
                  id="seatPlanPrice"
                  theme="dark"
                  size="big"
                  className={cx(css.planPrice)}
                >
                  <DollarSign />
                  <span>{spreadedPlan.price}</span>
                  <Text size="small" className={css.subtext}>
                    &nbsp; {spreadedPlan.subtext}
                  </Text>
                </Text>
                <InputNumber
                  name="seats"
                  ariaLabelledBy="seatPlanPrice"
                  value={seats}
                  onChange={(value) => setSeats(value)}
                  onIncrement={(value) =>
                    value < 999999
                      ? setSeats((prevValue) => prevValue + 1)
                      : null
                  }
                  onDecrement={() =>
                    setSeats((prevValue) => Math.max(prevValue - 1, 0))
                  }
                  incrementLabel="Add one seat"
                  decrementLabel="Remove one seat"
                />
              </div>
              <div className={css.features}>
                <Text theme="dark" size="small" style={{ marginBottom: '8px' }}>
                  {starFeatures[0].name}
                </Text>
                {starFeatures &&
                  starFeatures.slice(1).map(({ name, id, tooltip }) => (
                    <div key={id} className={css.feature}>
                      {tooltip ? (
                        <Tooltip position="right" content={tooltip}>
                          <CheckMark
                            className={css.checkMark}
                            theme={'green'}
                          />
                          <Text
                            className={cx(css.withTooltip)}
                            key={id}
                            size="small"
                            theme="dark"
                          >
                            {name}
                          </Text>
                        </Tooltip>
                      ) : (
                        <>
                          <CheckMark
                            className={css.checkMark}
                            theme={'green'}
                          />
                          <Text key={id} size="small" theme="dark">
                            {name}
                          </Text>
                        </>
                      )}
                    </div>
                  ))}
              </div>
              <hr />
              <div className={css.buyGrowthModal__details}>
                <Text
                  className={cx(css.planTitle, css.titleMobile)}
                  size="smaller"
                  theme="dark"
                >
                  Single Sign-On
                  <div className={cx(css.tag, css.tagPurple)}>ADD-ON</div>
                </Text>
              </div>
              <div className={css.buyGrowthModal__details}>
                <Text theme="dark" className={css.ssoPrice}>
                  $50{' '}
                  <Text size="smaller" className={css.subtext}>
                    &nbsp; per seat / month
                  </Text>
                </Text>
                <input
                  type="checkbox"
                  id="toggle"
                  name="toggle"
                  className={css.checkbox}
                  checked={isToggleOn}
                  onChange={() => setIsToggleOn(!isToggleOn)}
                />
                <label
                  tabIndex={0}
                  role="checkbox"
                  aria-checked={isToggleOn}
                  htmlFor="toggle"
                  className={css.switch}
                ></label>
              </div>
              <Text size="smaller" className={css.subtext}>
                Authenticate with Okta, Microsoft EntraID, or multiple other
                SAML 2.0 or OAuth2 providers.
              </Text>
            </div>
          </div>
          <div className={css.buyGrowthModal__details}>
            <Button
              className={css.button}
              theme="white"
              tagName="button"
              onClick={(e) => {
                e.preventDefault()
                closeModal()
              }}
            >
              Cancel
            </Button>
            <Button
              className={css.button}
              theme="blue"
              type="submit"
              tagName="button"
            >
              Continue
            </Button>
          </div>
        </div>
      </form>
    </div>,
    document.body
  )
}

export default BuyGrowthModal
